import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'

/**
 * Display ordering for a facility's (surface's) time slots.
 *
 * QA reported date-specific slots rendering in insertion order rather than
 * chronologically. The backend returned them in `created_at` order, which for a
 * CSV import is the order the rows happened to be written — not the order an
 * admin reads a rink schedule in.
 *
 * Rule: recurring slots first (input order preserved), then date-specific slots
 * by date, then start time, then a deterministic tie-breaker.
 */

/**
 * A slot is date-specific only when BOTH halves of the discriminator hold —
 * the exact conjunction TimeSlotRow renders its Frequency/Days columns on. A
 * `recurrence_type: 'specific'` row with no `slot_date` still renders as a
 * recurring row, so it must sort as one.
 */
export const isDateSpecificSlot = (slot: TimeSlot): boolean =>
  slot.recurrence_type === 'specific' && !!slot.slot_date

/**
 * Date key, tolerant of an ISO datetime ("2026-09-18T00:00:00Z") — the leading
 * "YYYY-MM-DD" compares chronologically as a plain string either way.
 */
const dateKey = (slot: TimeSlot): string => (slot.slot_date || '').slice(0, 10)

/**
 * Start time as minutes-of-day.
 *
 * Keyed off `time_ranges[0]` — the first range by `sort_order`, which is the
 * one TimeSlotRow displays — NOT a min() across ranges. Multi-range slots don't
 * exist in production today, but pinning the same rule here and in api2's
 * `displayOrderKey()` is what makes the two orders agree.
 *
 * Always finite: a missing, empty or unparseable range yields 0, matching the
 * '00:00 AM' TimeSlotRow renders for one. A NaN must never reach the comparator
 * — a NaN comparator result is undefined behaviour in Array.prototype.sort.
 */
const startMinutes = (slot: TimeSlot): number => {
  const raw = slot.time_ranges?.[0]?.start_time
  if (!raw) {
    return 0
  }
  const [rawHours, rawMinutes] = raw.split(':')
  const hours = Number(rawHours)
  const minutes = Number(rawMinutes)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return 0
  }
  return hours * 60 + minutes
}

/**
 * `created_at` as an epoch. Invalid/missing sorts last (+Infinity) rather than
 * first, so a row the API didn't stamp doesn't jump the queue.
 */
const createdAtKey = (slot: TimeSlot): number => {
  const parsed = Date.parse(slot.created_at || '')
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
}

/**
 * Comparator for two date-specific slots: date → start time → created_at → id.
 *
 * Uses `<`/`>` rather than subtraction throughout: `Infinity - Infinity` is NaN,
 * and localeCompare would make the order locale-dependent (CI vs browser).
 */
export const compareDateSpecificSlots = (a: TimeSlot, b: TimeSlot): number => {
  const dateA = dateKey(a)
  const dateB = dateKey(b)
  if (dateA !== dateB) {
    return dateA < dateB ? -1 : 1
  }

  const startA = startMinutes(a)
  const startB = startMinutes(b)
  if (startA !== startB) {
    return startA < startB ? -1 : 1
  }

  const createdA = createdAtKey(a)
  const createdB = createdAtKey(b)
  if (createdA !== createdB) {
    return createdA < createdB ? -1 : 1
  }

  if (a.id === b.id) {
    return 0
  }
  return a.id < b.id ? -1 : 1
}

/**
 * Recurring slots first (in the order they arrived), then date-specific slots
 * in chronological order. Never mutates the input — partitions into fresh
 * arrays rather than relying on sort stability for the recurring block.
 */
export const sortFacilityTimeSlots = (slots: TimeSlot[]): TimeSlot[] => {
  const recurring: TimeSlot[] = []
  const dateSpecific: TimeSlot[] = []

  for (const slot of slots) {
    if (isDateSpecificSlot(slot)) {
      dateSpecific.push(slot)
    } else {
      recurring.push(slot)
    }
  }

  dateSpecific.sort(compareDateSpecificSlots)

  return [...recurring, ...dateSpecific]
}
